﻿namespace eXpress.core {
	export interface IPhotoImport {
		selection: IPhoto[] | null;
		storeType: StoreType;
	}

	export interface IImporterDependencies {
		readonly contentManager: core.ContentManager;
		readonly storageService: services.StorageService;
		readonly uploadService: services.UploadService;
		readonly productCategory: string;
		readonly processSelectedPhotosAsync: (data: IPhotoImport | null) => Promise<void>;
	}

	export class photoStorePopupOptions {
		readonly errorMessage?: string = undefined;
	}

	export class Importer {
		private readonly IMPORTFROM_CLASS: string = 'technical-importfrom';
		private readonly contentManager: core.ContentManager;
		private readonly storageService: services.StorageService;
		private readonly uploadService: services.UploadService;
		private readonly productCategory: string;

		private currentStoreType: StoreType;
		private externalPicker: eXpress.core.mediastore.ExternalPhotoPicker | undefined;
		private pickerWindow: Window | null;

		private photoStoreConnector: PhotoStoreConnector;
		private selectPhotoSourcePopup: SelectPhotoSourcePopup;
		private uploadProgressPopup: ProgressPopup;
		private uploader: Upload;
		public processSelectedPhotosAsync: (data: IPhotoImport | null) => Promise<void>;
		private broadcastChannel?: BroadcastChannel;

		constructor(deps: IImporterDependencies) {
			this.productCategory = deps.productCategory;
			this.contentManager = deps.contentManager;
			this.storageService = deps.storageService;
			this.uploadService = deps.uploadService;
			this.processSelectedPhotosAsync = deps.processSelectedPhotosAsync;
		}

		public async openAsync(popupOptions?: photoStorePopupOptions): Promise<void> {
			this.photoStoreConnector = new PhotoStoreConnector({
				connectedToStoreAsync: this.connectedToStoreAsync,
				storageService: this.storageService,
			} as IPhotoStoreConnectorDependencies);


			this.createUploader();
			// delete popup if it already exists
			if (this.selectPhotoSourcePopup) {
				this.selectPhotoSourcePopup.delete();
			}
			this.selectPhotoSourcePopup = await core.showLoadingDialogPromiseAsync(this.createSelectPhotoSourcePopupAsync());

			if (popupOptions?.errorMessage) {
				const pagelog = html.querySelector<HTMLElement>(this.selectPhotoSourcePopup.popup, '.technical-top-message-log', false);
				if (pagelog) {
					const pageLogComponent = components.main.createPageLog(pagelog);
					pageLogComponent.updateMessageAndStyle(components.main.PageLogStyle.Error, popupOptions?.errorMessage);
				}
			}

			// start listening for messages
			if (!this.broadcastChannel) {
				this.broadcastChannel = new BroadcastChannel("connectedToStorePartner");
				this.broadcastChannel.onmessage = this.messageEvent;
			}

			await this.selectPhotoSourcePopup.openAsync();
		}

		// functions
		private createSelectPhotoSourcePopupAsync = async (): Promise<SelectPhotoSourcePopup> => {
			const stores = await this.storageService.getPhotoStoresAsync(context.pointOfSaleId, context.languageCode, false, false, true);
			const $popup = await SelectPhotoSourcePopup.createDialogAsync(this.contentManager, stores);

			return new SelectPhotoSourcePopup($popup, stores, this.contentManager, context, this.uploader, this.connectToStorePartnerAsync, this.popupCloseEvent);
		};

		public connectToStorePartnerAsync = async (store: IPhotoStore | null): Promise<void> => {
			if (!store)
				return;

			this.currentStoreType = store.storeType;

			// eXpress => close the popup so we can display the login-popup
			if (store.storeType == StoreType.eXpress)
				this.selectPhotoSourcePopup.close(null);

			await this.photoStoreConnector.connectToAsync(store, store.useExternalPhotoPicker);
		};

		private connectedToStoreAsync = async (store: IPhotoStore | null): Promise<void> => {
			if (!store || !store.isConnected)
				return;

			this.selectPhotoSourcePopup.reloadStore(store);

			if (this.currentStoreType && this.currentStoreType != store.storeType)
				await this.closeConnectionAsync();

			this.currentStoreType = store.storeType;
			if (store.storeType === StoreType.GooglePhotos && store.useExternalPhotoPicker) {
				await this.openImportFromGoogleAsync(store);
				return;
			}

			this.selectPhotoSourcePopup.close(null);
			this.openImportFrom(store);
		};

		private popupCloseEvent = () => {
			//Close the connection to the partner
			this.closeConnectionAsync();
		};

		public closeConnectionAsync = async () => {
			if (this.externalPicker) {
				await this.externalPicker.deleteExternalPhotoPicker();
				this.externalPicker = undefined;
			}

			this.pickerWindow && this.pickerWindow.close();
			this.pickerWindow = null;
		};

		private showPopupBlockedBanner = (store: IPhotoStore) => {
			const bannerEl = document.querySelector<HTMLElement>('.technical-mediapartner-connected-popupblocked');
			if (!bannerEl)
				return;

			bannerEl.innerHTML = bannerEl.innerHTML.replace('{0}', store.name);
			bannerEl.classList.remove("u-hide");
		};

		private openImportFromGoogleAsync = async (store: IPhotoStore) => {
			// reuse existing picker
			if (!this.externalPicker)
				this.externalPicker = new eXpress.core.mediastore.ExternalPhotoPicker(this.storageService);

			await core.showLoadingDialogPromiseAsync(this.externalPicker.createExternalPhotoPicker());

			// a window is already open? Try to close it
			if (this.pickerWindow)
				this.pickerWindow.close();

			// open the picker
			this.pickerWindow = this.externalPicker.openPicker();
			if (this.pickerWindow == null) {
				// We couldn't open the popup => show the banner
				// The customer could open the popup via the browser, so we can't stop here
				// and have to start polling the session
				this.showPopupBlockedBanner(store);
			}

			// wait for feedback
			const userHasSelectedPhotos = await this.externalPicker.startPickerPolling();
			if (!userHasSelectedPhotos)
				return;

			const selectPhotosAndCloseConnection = async (externalPicker: eXpress.core.mediastore.ExternalPhotoPicker): Promise<partners.PartnerPhoto[]> => {
				const photos = await externalPicker.getSelectedPhotos();
				await this.closeConnectionAsync();
				return photos;
			};

			const photos = await core.showLoadingDialogPromiseAsync(selectPhotosAndCloseConnection(this.externalPicker));
			this.selectPhotoSourcePopup.close(null); //Close the store selection-popup when the user has made his selection

			this.processSelectedPhotosAsync && await this.processSelectedPhotosAsync({ selection: photos, storeType: StoreType.GooglePhotos } as IPhotoImport);
		};

		public openImportFrom = (store: core.IPhotoStore) => {
			const renderedDialogsEl = document.getElementsByClassName('renderedDialogs');
			const htmlEl = html.querySelector(document, 'html', true);
			const bodyEl = html.querySelector(document, 'body', true);

			html.addClass(htmlEl, 'has-open-dialog u-no-scroll');
			html.addClass(bodyEl, 'u-no-scroll');

			if (!renderedDialogsEl || renderedDialogsEl.length == 0)
				return;

			const renderedDialogEl = renderedDialogsEl[0];

			// create iframe
			const frameEl = document.createElement("iframe");
			frameEl.style.height = "100%;";
			frameEl.style.overflow = 'hidden';
			frameEl.style.width = '100%';
			frameEl.style.height = '100vh';
			frameEl.src = store.importPhotosUrl;

			// create content-div
			const corePopupContent = document.createElement("div");
			corePopupContent.classList.add('m-popup__content');
			corePopupContent.style.maxWidth = 'initial';
			corePopupContent.style.display = 'flex';
			corePopupContent.style.maxHeight = '100vh';
			corePopupContent.append(frameEl);

			// create container-div
			const corePopup = document.createElement("div");
			corePopup.classList.add('m-popup');
			corePopup.classList.add(this.IMPORTFROM_CLASS);
			corePopup.style.display = 'flex';
			corePopup.append(corePopupContent);

			renderedDialogEl.append(corePopup);
		};

		private importPhotosFinished = async (photoImport: IPhotoImport | null) => {
			this.processSelectedPhotosAsync && await this.processSelectedPhotosAsync(photoImport);
			await this.closeImportFromAsync(photoImport?.storeType);
		};

		private messageEvent = async (e) => {
			if (e.origin !== window.origin) return;
			if (!e.data || !e.data.command) return;

			switch (e.data.command) {
				case "CloseImportPhotosPopUp":
					await this.closeImportFromAsync();
					break;
				case "ImportPhotosBack": {
					await this.closeImportFromAsync();
					await this.openAsync();
					break;
				}
				case "ImportPhotosFinished": {
					const photoImport = e.data.detail as IPhotoImport | null;
					await this.importPhotosFinished(photoImport);
					break;
				}
				case "ImportPhotosPopUpError": {
					let photoStoreErrorMessage = "";
					if (e.data.detail.errorCode == "unableToConnectToPartner") {
						photoStoreErrorMessage = await this.contentManager.getLabelAsync(
							"Label.AlbumMain." + StoreType[this.currentStoreType] + ".CouldNotConnect",
						);
					}

					await this.closeImportFromAsync();
					await this.openAsync({ errorMessage: photoStoreErrorMessage });
					break;
				}
			}
		};

		public closeImportFromAsync = async (storeType?: StoreType) => {
			// remove popup
			document.querySelectorAll('.' + this.IMPORTFROM_CLASS).forEach(importFromEl => importFromEl.remove());

			const htmlEl = html.querySelector(document, 'html', true);
			const bodyEl = html.querySelector(document, 'body', true);

			html.removeClass(htmlEl, 'has-open-dialog u-no-scroll');
			html.removeClass(bodyEl, 'u-no-scroll');

			// close connection
			await this.closeConnectionAsync();
		};


		// upload stuff

		private createUploader(): void {
			if (this.uploader) {
				this.uploader.refresh();
				return;
			}

			const uploadButtonId = 'uploadFromDeviceBtn';
			const uploadSettings: eXpress.core.UploadSettings = {
				uploadButtonId: uploadButtonId,
				afterInit: () => {
					const uploadBtn = document.getElementById(uploadButtonId);
					uploadBtn && uploadBtn.addEventListener('click', async (e) => {
						// close any open connection
						await this.closeConnectionAsync();
					});
				},
				beforeUploadStart: (totalFiles) => {
					this.selectPhotoSourcePopup.makeInvisible();

					core.showLoadingDialogAsync(ProgressPopup.createDialogAsync())
						.then($popup => {
							this.uploadProgressPopup = new ProgressPopup($popup);
							this.uploadProgressPopup.setProgressDialogContentAsync(this.contentManager, "Label.UploadProgressDialog.Counter", totalFiles)
								.then(progressPopup => {
									// only show progress if upload did not finish yet!
									if (this.uploader.isUploading()) {
										this.uploadProgressPopup.openAsync();
									}
								});
						});
				},
				fileUploaded: (uploadedFiles) => {
					// update upload progress
					if (this.uploadProgressPopup)
						this.uploadProgressPopup.updateProgress(uploadedFiles);
				},
				importFinished: (folderId: string, importedFileIds: string[], numberOfFailedUploads: number, failedFileNames: string[]) => {
					this.selectPhotoSourcePopup.close(null); // plupload fails if the element is removed, so only remove it after all files are uploaded

					if (this.uploadProgressPopup)
						this.uploadProgressPopup.close(true);

					this.uploader.refresh();
					if (numberOfFailedUploads > 0) {
						core.showLoadingDialogAsync(this.uploader.getUploadErrorPopupAsync(this.contentManager, numberOfFailedUploads, failedFileNames))
							.then($popup => new ContentPopup($popup, true).openAsync())
							.then(() => this.getUploadedPhotosAsync(folderId, importedFileIds))
							.then(async (photos: AnyPhoto[] | undefined) => {
								const photoImport = {
									selection: photos,
									storeType: StoreType.eXpress,
								} as IPhotoImport;
								this.processSelectedPhotosAsync && await this.processSelectedPhotosAsync(photoImport);
							});
					}
					else {
						this.getUploadedPhotosAsync(folderId, importedFileIds)
							.then(async (photos: AnyPhoto[] | undefined) => {
								const photoImport = {
									selection: photos,
									storeType: StoreType.eXpress,
								} as IPhotoImport;
								this.processSelectedPhotosAsync && await this.processSelectedPhotosAsync(photoImport);
							});
					}
				},
				createUploadSessionAsync: (runtime) => {
					return this.getUploadFolderAsync().then(folder => {
						if (!folder.id) throw new Error("Upload folder needs to have an id.");

						this.uploader.setFolderId(folder.id);
						return this.uploadService.setToken().then(_ => {
							return this.uploadService.createUploadSessionAsync(context.pointOfSaleId, GetCookie("accid"), folder.id!, "prints", runtime);
						});
					});
				}
			};

			this.uploader = new Upload(uploadSettings, context, this.uploadService, "prints");
		}

		private getUploadedPhotosAsync = async (folderId: string, fileIds: string[]): Promise<AnyPhoto[] | undefined> => {
			if (!fileIds || !fileIds.length)
				return undefined;

			return await this.storageService.getPhotosAsync(context.pointOfSaleId, GetCookie("accid"), folderId, fileIds);
		};

		private getUploadFolderAsync(): Promise<IPhotoFolder> {
			return this.uploadService.setToken().then(_ => {
				return this.uploadService.getUploadFolderForProductCategoryAsync(GetCookie("accid"), this.productCategory);
			});

		}
	}
}
